import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { RGB } from '@rapoo/device-sdk'

export type DeviceIdentifier = {
  vid: number
  /** 单个 pid，或设备 hardware.productId 的 pid 列表 */
  pid: number | number[]
}

type DeviceColorPreference = DeviceIdentifier & {
  colorKey: string
}

/** 底座铭牌灯在开关关闭期间冻结的"展示中"灯效快照，刷新后预览仍显示关闭前的灯效 */
export type BaseLightingDisplaySnapshot = DeviceIdentifier & {
  currentEffectId: number
  brightness: number
  speed: number
  rgb: RGB
}

/** 底座灯带与铭牌灯同步灯效开关 */
type DeviceLightingSyncPreference = DeviceIdentifier & {
  enabled: boolean
}

const MAX_DEVICE_COLOR_PREFERENCES = 20
const MAX_LIGHTING_DISPLAY_SNAPSHOTS = 20
const MAX_LIGHTING_SYNC_PREFERENCES = 20

function toPidList(pid: number | number[]): number[] {
  return Array.isArray(pid) ? pid : [pid]
}

/** 同 vid 下，任一侧 pid 落在另一侧 pid 列表中即视为同一设备 */
function matchesDevice(preference: DeviceIdentifier, device: DeviceIdentifier): boolean {
  if (preference.vid !== device.vid) return false
  const devicePids = toPidList(device.pid)
  return toPidList(preference.pid).some((pid) => devicePids.includes(pid))
}

export const useDevicePreferencesStore = defineStore(
  'device-preferences',
  () => {
    const colorPreferences = ref<DeviceColorPreference[]>([])
    const lightingDisplaySnapshots = ref<BaseLightingDisplaySnapshot[]>([])
    const lightingSyncPreferences = ref<DeviceLightingSyncPreference[]>([])

    function getColor(device: DeviceIdentifier): string | undefined {
      const preferenceIndex = colorPreferences.value.findIndex((preference) =>
        matchesDevice(preference, device),
      )
      if (preferenceIndex === -1) return

      const preference = colorPreferences.value[preferenceIndex]
      colorPreferences.value = [
        ...colorPreferences.value.slice(0, preferenceIndex),
        ...colorPreferences.value.slice(preferenceIndex + 1),
        preference,
      ]
      return preference.colorKey
    }

    function setColor(device: DeviceIdentifier, colorKey: string) {
      colorPreferences.value = [
        ...colorPreferences.value.filter((preference) => !matchesDevice(preference, device)),
        { ...device, colorKey },
      ].slice(-MAX_DEVICE_COLOR_PREFERENCES)
    }

    function removeColor(device: DeviceIdentifier) {
      colorPreferences.value = colorPreferences.value.filter(
        (preference) => !matchesDevice(preference, device),
      )
    }

    function getLightingDisplay(
      device: DeviceIdentifier,
    ): Omit<BaseLightingDisplaySnapshot, 'vid' | 'pid'> | undefined {
      const snapshotIndex = lightingDisplaySnapshots.value.findIndex((snapshot) =>
        matchesDevice(snapshot, device),
      )
      if (snapshotIndex === -1) return

      const snapshot = lightingDisplaySnapshots.value[snapshotIndex]
      lightingDisplaySnapshots.value = [
        ...lightingDisplaySnapshots.value.slice(0, snapshotIndex),
        ...lightingDisplaySnapshots.value.slice(snapshotIndex + 1),
        snapshot,
      ]
      return {
        currentEffectId: snapshot.currentEffectId,
        brightness: snapshot.brightness,
        speed: snapshot.speed,
        rgb: snapshot.rgb,
      }
    }

    function setLightingDisplay(
      device: DeviceIdentifier,
      snapshot: Omit<BaseLightingDisplaySnapshot, 'vid' | 'pid'>,
    ) {
      lightingDisplaySnapshots.value = [
        ...lightingDisplaySnapshots.value.filter((item) => !matchesDevice(item, device)),
        { ...device, ...snapshot },
      ].slice(-MAX_LIGHTING_DISPLAY_SNAPSHOTS)
    }

    function removeLightingDisplay(device: DeviceIdentifier) {
      lightingDisplaySnapshots.value = lightingDisplaySnapshots.value.filter(
        (snapshot) => !matchesDevice(snapshot, device),
      )
    }

    function getLightingSync(device: DeviceIdentifier): boolean | undefined {
      const preferenceIndex = lightingSyncPreferences.value.findIndex((preference) =>
        matchesDevice(preference, device),
      )
      if (preferenceIndex === -1) return

      const preference = lightingSyncPreferences.value[preferenceIndex]
      lightingSyncPreferences.value = [
        ...lightingSyncPreferences.value.slice(0, preferenceIndex),
        ...lightingSyncPreferences.value.slice(preferenceIndex + 1),
        preference,
      ]
      return preference.enabled
    }

    function setLightingSync(device: DeviceIdentifier, enabled: boolean) {
      lightingSyncPreferences.value = [
        ...lightingSyncPreferences.value.filter((preference) => !matchesDevice(preference, device)),
        { ...device, enabled },
      ].slice(-MAX_LIGHTING_SYNC_PREFERENCES)
    }

    return {
      colorPreferences,
      lightingDisplaySnapshots,
      lightingSyncPreferences,
      getColor,
      setColor,
      removeColor,
      getLightingDisplay,
      setLightingDisplay,
      removeLightingDisplay,
      getLightingSync,
      setLightingSync,
    }
  },
  {
    persist: {
      key: 'rapoo-device-preferences',
      pick: ['colorPreferences', 'lightingDisplaySnapshots', 'lightingSyncPreferences'],
    },
  },
)
